import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Badge, 
  Box, 
  Button, 
  Divider, 
  IconButton, 
  Menu, 
  MenuItem, 
  Snackbar,
  Tooltip,
  Typography,
  CircularProgress
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { DeleteOutline, DoneAll, Event } from '@mui/icons-material';
import { useAppointmentsUpcomingRetrieveQuery } from '../../../services/GLOWClient';
import { useNotificationsWebSocket } from '../../../hooks/useNotificationsWebSocket';
import { useAuth } from '../../../context/AuthContext';

const FutureAppointmentDropdown = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const { data: upcomingAppointments, isLoading } = useAppointmentsUpcomingRetrieveQuery();
  const [unseenCount, setUnseenCount] = useState(0);

  // Track which appointment IDs have been seen locally
  const [seenIds, setSeenIds] = useState(new Set());
  const [isSeenIdsLoaded, setIsSeenIdsLoaded] = useState(false); // Add loading state
  const STORAGE_KEY = 'client_seen_appointments';
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [isDeletedIdsLoaded, setIsDeletedIdsLoaded] = useState(false);
  const DELETED_STORAGE_KEY = 'client_deleted_appointments';

  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderText, setReminderText] = useState('');

  const wsToken = useMemo(() => {
    return user?.token || localStorage.getItem('access_token') || '';
  }, [user]);

  const wsEnabled = Boolean(wsToken) && isAuthenticated();

  // Load seen IDs from localStorage once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          setSeenIds(new Set(arr.map((v) => Number(v))));
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    setIsSeenIdsLoaded(true); // Mark as loaded
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DELETED_STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          setDeletedIds(new Set(arr.map((v) => Number(v))));
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    setIsDeletedIdsLoaded(true);
  }, []);

  // Persist seen IDs whenever they change (but only after initial load)
  useEffect(() => {
    if (!isSeenIdsLoaded) return; // Don't save until we've loaded from localStorage
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seenIds)));
    } catch (e) {
      // ignore storage errors
    }
  }, [seenIds, isSeenIdsLoaded]);

  useEffect(() => {
    if (!isDeletedIdsLoaded) return;

    try {
      localStorage.setItem(DELETED_STORAGE_KEY, JSON.stringify(Array.from(deletedIds)));
    } catch (e) {
      // ignore storage errors
    }
  }, [deletedIds, isDeletedIdsLoaded]);

  // Sync unseen count with fetched appointments and local seen state
  // Only calculate after seenIds are loaded from localStorage
  useEffect(() => {
    if (!isSeenIdsLoaded || !isDeletedIdsLoaded) return; // Wait for ids to be loaded

    const confirmed = Array.isArray(upcomingAppointments)
      ? upcomingAppointments.filter((a) => a?.status === 'confirmed')
      : [];

    const visible = confirmed.filter((a) => !deletedIds.has(Number(a.appointment_id)));

    if (visible.length > 0) {
      const total = visible.length;
      const seenInList = visible.reduce(
        (acc, a) => acc + (seenIds.has(Number(a.appointment_id)) ? 1 : 0),
        0
      );
      setUnseenCount(Math.max(0, total - seenInList));
    } else {
      setUnseenCount(0);
    }
  }, [upcomingAppointments, seenIds, deletedIds, isSeenIdsLoaded, isDeletedIdsLoaded]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('locale'), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed':
        return 'success.main';
      case 'pending':
        return 'warning.main';
      case 'cancelled':
        return 'error.main';
      case 'completed':
        return 'info.main';
      default:
        return 'text.secondary';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch(status) {
      case 'confirmed':
        return t('client_status.confirmed');
      case 'pending':
        return t('client_status.pending');
      case 'cancelled':
        return t('client_status.cancelled');
      case 'completed':
        return t('client_status.completed');
      default:
        return status;
    }
  };

  const appointmentsCount = unseenCount;

  const confirmedUpcomingAppointments = Array.isArray(upcomingAppointments)
    ? upcomingAppointments.filter((a) => a?.status === 'confirmed')
    : [];

  const visibleAppointments = confirmedUpcomingAppointments.filter(
    (a) => !deletedIds.has(Number(a.appointment_id))
  );

  const handleWsMessage = useCallback((payload) => {
    if (!payload) return;
    if (payload.event !== 'notification.created' || !payload.notification) return;

    const n = payload.notification;
    if (n.type === 'appointment_reminder') {
      const msg = n.message || 'Rappel : vous avez un rendez-vous dans 1 heure.';
      setReminderText(msg);
      setReminderOpen(true);
    }
  }, []);

  useNotificationsWebSocket({ enabled: wsEnabled, token: wsToken, onMessage: handleWsMessage });

  return (
    <Box>
      <Snackbar
        open={reminderOpen}
        autoHideDuration={12000}
        onClose={() => setReminderOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setReminderOpen(false)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {reminderText}
        </Alert>
      </Snackbar>

      <IconButton
        size="large"
        aria-label="appointments"
        aria-controls="menu-appointments"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
      >
        <Badge badgeContent={appointmentsCount} color="error">
          <Event />
        </Badge>
      </IconButton>
      <Menu
        id="menu-appointments"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {t('appointments_dropdown.title')}
          </Typography>
        </Box>
        <Divider />
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : visibleAppointments.length > 0 ? (
          <div>
            <Box
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DoneAll />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const firstFive = visibleAppointments.slice(0, 5);
                    setSeenIds((prev) => {
                      const next = new Set(prev);
                      firstFive.forEach((a) => next.add(Number(a.appointment_id)));
                      return next;
                    });
                  }}
                  sx={{ 
                    display: { xs: 'none', sm: 'flex' }
                  }}
                >
                  {t('notifications.mark_all_as_read')}
                </Button>
                <Tooltip title={t('notifications.mark_all_as_read')}>
                  <IconButton
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const firstFive = visibleAppointments.slice(0, 5);
                      setSeenIds((prev) => {
                        const next = new Set(prev);
                        firstFive.forEach((a) => next.add(Number(a.appointment_id)));
                        return next;
                      });
                    }}
                    sx={{ 
                      display: { xs: 'inline-flex', sm: 'none' },
                      border: '1px solid rgba(0, 0, 0, 0.23)'
                    }}
                  >
                    <DoneAll fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const firstFive = visibleAppointments.slice(0, 5);
                    setDeletedIds((prev) => {
                      const next = new Set(prev);
                      firstFive.forEach((a) => next.add(Number(a.appointment_id)));
                      return next;
                    });
                  }}
                  sx={{ 
                    display: { xs: 'none', sm: 'flex' }
                  }}
                >
                  {t('notifications.delete_all')}
                </Button>
                <Tooltip title={t('notifications.delete_all')}>
                  <IconButton
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const firstFive = visibleAppointments.slice(0, 5);
                      setDeletedIds((prev) => {
                        const next = new Set(prev);
                        firstFive.forEach((a) => next.add(Number(a.appointment_id)));
                        return next;
                      });
                    }}
                    sx={{ 
                      display: { xs: 'inline-flex', sm: 'none' },
                      border: '1px solid rgba(211, 47, 47, 0.5)'
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {visibleAppointments.slice(0, 5).map((appointment) => {
              const id = Number(appointment.appointment_id);
              const isSeen = seenIds.has(id);

              return (
                <MenuItem
                  key={appointment.appointment_id}
                  sx={{
                    py: 1.5,
                    borderLeft: `4px solid ${getStatusColor(appointment.status)}`,
                    backgroundColor: isSeen ? 'transparent' : 'action.hover',
                    '&:hover': { backgroundColor: 'action.selected' },
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      component={Link}
                      to={`/client/appointments/${appointment.appointment_id}`}
                      onClick={() => {
                        handleClose();
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textDecoration: 'none',
                        color: 'inherit',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: isSeen ? 'regular' : 'bold' }}
                        >
                          <strong>{t('appointments_dropdown.service')}: </strong> {appointment.service_name || `${t('appointments_dropdown.service')} #${appointment.service}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getStatusLabel(appointment.status)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('appointments_dropdown.date')}: </strong>{formatDate(appointment.start_time)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>{t('appointments_dropdown.salon')}: </strong>{appointment.salon_name || `${t('appointments_dropdown.salon')} #${appointment.salon}`}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {!isSeen && (
                        <Tooltip title={t('notifications.mark_as_read')}>
                          <IconButton
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSeenIds((prev) => {
                                const next = new Set(prev);
                                next.add(id);
                                return next;
                              });
                            }}
                            sx={{ 
                              border: '1px solid rgba(0, 0, 0, 0.23)',
                              p: 0.5
                            }}
                          >
                            <DoneAll fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('notifications.delete_all')}>
                        <IconButton
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeletedIds((prev) => {
                              const next = new Set(prev);
                              next.add(id);
                              return next;
                            });
                          }}
                          sx={{ 
                            border: '1px solid rgba(211, 47, 47, 0.5)',
                            p: 0.5
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
            <Divider />
            <MenuItem 
              component={Link} 
              to="/client/appointments" 
              onClick={() => {
                // Mark all as seen locally when viewing all
                if (Array.isArray(visibleAppointments)) {
                  const allIds = new Set(seenIds);
                  visibleAppointments.forEach(a => allIds.add(Number(a.appointment_id)));
                  setSeenIds(allIds);
                }
                handleClose();
              }}
              sx={{ justifyContent: 'center', color: 'primary.main' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {t('appointments_dropdown.view_all')}
              </Typography>
            </MenuItem>
          </div>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('appointments_dropdown.no_appointments')}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              component={Link} 
              to="/client/salons"
              onClick={handleClose}
            > 
              {t('appointments_dropdown.book_now')}
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default FutureAppointmentDropdown;